import { readSessionValue, writeSessionValue } from './browserStorage.js';

export interface BrowserSelection {
  courseId: string;
  groupId: number | null;
}

const BROWSER_SELECTION_STORAGE_KEY = 'author.browser.selection';

export function buildCourseBrowserPath(courseId: string, groupId: number | null = null) {
  const search = new URLSearchParams();
  if (courseId) {
    search.set('courseId', courseId);
  }
  if (groupId != null) {
    search.set('groupId', String(groupId));
  }

  const query = search.toString();
  return query ? `/courses?${query}` : '/courses';
}

export function buildEntityEditorPath(courseId: string, entityId: number, groupId: number | null = null) {
  const path = `/courses/${encodeURIComponent(courseId)}/entities/${entityId}`;
  if (groupId == null) {
    return path;
  }

  return `${path}?groupId=${groupId}`;
}

export function parseGroupId(value: string | null) {
  if (!value) {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Every web-storage access here goes through `lib/browserStorage.ts`, which guards three different
 * failures at once — an access that THROWS (a private window, or a browser set to block site data)
 * an object that exists with no methods on it (this repo's jsdom, where the same shape appears for
 * `localStorage`), and a method that throws when CALLED. Unguarded, the first of those crashed the Courses screen outright: this file
 * called `window.sessionStorage.getItem` on the browser's first render with no `try` around it,
 * while its newer neighbour `lib/courseSelection.ts` guarded its own access correctly. Reported by
 * `gpt-5.6-sol` on 2026-08-28; the rule now lives in one place rather than in two habits.
 */
export function readBrowserSelection(): BrowserSelection | null {
  const raw = readSessionValue(BROWSER_SELECTION_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<BrowserSelection>;
    if (typeof parsed.courseId !== 'string') {
      return null;
    }

    return {
      courseId: parsed.courseId,
      groupId: typeof parsed.groupId === 'number' && Number.isFinite(parsed.groupId) ? parsed.groupId : null,
    };
  } catch {
    return null;
  }
}

export function writeBrowserSelection(selection: BrowserSelection) {
  writeSessionValue(BROWSER_SELECTION_STORAGE_KEY, JSON.stringify(selection));
}

export function replaceCurrentBrowserSelection(courseId: string, groupId: number | null) {
  if (typeof window === 'undefined') {
    return;
  }

  window.history.replaceState(window.history.state, '', buildCourseBrowserPath(courseId, groupId));
}
